import React, { useEffect } from 'react'
import ModalLayout from '../shared/ModalLayout'
import AddActivityFooter from './AddActivityFooter'
import AddActivityHeader from './AddActivityHeader'
import AddActivitiesForm from '../../forms/activities/AddActivitiesForm'
import { ActivityModel } from '@/app/models'
import useDriverActivityService from '../../services/DriverActivityService'
import { useActivity } from '@/modules/drivers/stores/activityStore'
import {
  getActivityTypeNumber,
} from '../../helpers/ActivityTypeResolver'
import { AddActivityMode } from '@/app/constants/addActivityMode'
import { ActivityType } from '@/app/constants/activity'
import LoadingModal from '@/modules/shared/components/LoadingModal'
import { ReportType } from '@/app/constants/reportType'
import { TaskType } from '@/app/constants/taskType'
import usePrintService from '../../services/PrintService'
import { useTask } from '@/modules/shared/context/TaskContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import moment from 'moment'

type AddActivityPopupProps = {
  activity: ActivityModel
  translate: (string) => any
  onClose: () => void
  driverName: string
  onRefresh: () => void
  companyId: string
  driverId: number
  type: 'add' | 'edit' | 'multi-edit'
  activities?: ActivityModel[]
}

const AddActivityModal: React.FC<AddActivityPopupProps> = ({
  activity,
  translate,
  companyId,
  driverId,
  onClose,
  driverName,
  onRefresh,
  type,
  activities,
}) => {
  const { data, setData } = useActivity()
  const { addActivity, editActivity } = useDriverActivityService()
  const [loading, setLoading] = React.useState(false)
  const [printAfterSave, setPrintAfterSave] = React.useState(false)
  const printService = usePrintService()
  const { addTask } = useTask()
  const { t } = useLocalize()
  useEffect(() => {
    if (activity) {
      setData({
        ...data,
        startDate: activity.startTime,
        endDate: activity.endTime,
        activityType: activity.type,
      })
    }

    return () => {
      setData({
        ...data,
        startDate: null,
        endDate: null,
        overrideType: AddActivityMode.UNKNOWN,
        activityType: getActivityTypeNumber(ActivityType.AVAILABILITY),
      })
    }
  }, [activity])

  const handleAddActivitySubmit = async () => {
    setLoading(true)

    try {
      const activityToAdd = {
        ...activity,
        startTime: data.startDate,
        endTime: data.endDate,
        type: data.activityType,
      }

      await addActivity(companyId, driverId, activityToAdd, data.overrideType)
      onRefresh()
      onClose()
    } catch (error) {
      console.error('Error adding activity:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleEditActivitySubmit = async () => {
    setLoading(true)
    try {
      const activityToEdit = {
        ...activity,
        startTime: data.startDate,
        endTime: data.endDate,
        type: data.activityType,
      }

      await editActivity(companyId, driverId, activityToEdit)

      if (onRefresh) {
        await onRefresh()
      }

      onClose()
    } catch (error) {
      console.error('Error editing activity:', error)
    } finally {
      setLoading(false)
    }
  }

  const doSaveByType = async () => {
    if (type === 'add') {
      await handleAddActivitySubmit()
    } else if (type === 'multi-edit') {
      await handleMultiEditSubmit()
    } else {
      await handleEditActivitySubmit()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (printAfterSave) {
      handlePrint()
    } else {
      doSaveByType()
    }
  }

  const handleMultiEditSubmit = async () => {
    if (!activities || activities.length === 0) return
    setLoading(true)
    try {
      for (const act of activities) {
        const activityToEdit = {
          ...act,
          type: data.activityType,
        }
        await editActivity(companyId, driverId, activityToEdit)
      }
      if (onRefresh) {
        await onRefresh()
      }
      onClose()
    } catch (error) {
      console.error('Error editing activities:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePrint = async () => {
    const isEdit = type === 'edit'
    const originalStartTime = isEdit
      ? (activity.originalStartTime ?? activity.startTime)
      : data.startDate
    const originalEndTime = isEdit
      ? (activity.originalEndTime ?? activity.endTime)
      : data.endDate

    setLoading(true)
    try {
      const taskId = await printService.getPdf({
        companyId,
        reportType: ReportType.ACTIVITY_CHANGE_CONFIRMATION_REPORT,
        drivers: [driverId.toString()],
        startTime: moment(originalStartTime),
        endTime: moment(originalEndTime),
        data: {
          newType: data.activityType,
          originalActivity: {
            driverId,
            type: isEdit ? activity.type : data.activityType,
            startTime: isEdit ? activity.startTime : data.startDate,
            endTime: isEdit ? activity.endTime : data.endDate,
            originalStartTime,
            originalEndTime,
            vehicleRegistrationNumber: activity?.registrationNumber ?? null,
          },
        },
      })
      addTask(
        taskId,
        TaskType.Printout,
        ReportType.ACTIVITY_CHANGE_CONFIRMATION_REPORT,
      )
      toast.success({
        text: t('printout.start-generation-title'),
      })

      await doSaveByType()
    } catch (error) {
      toast.error({
        text: t('printout.generation-error'),
      })
      setLoading(false)
    }
  }
  return (
    <ModalLayout customZIndex={9999}>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="tw-relative  tw-flex tw-h-fit tw-max-h-[90vh] tw-w-full tw-max-w-[560px] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-2xl"
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        {loading && <LoadingModal title={t('operation.saving')} message={""} />}

        <AddActivityHeader close={onClose} driverName={driverName} />
        <AddActivitiesForm
          activitiesToEdit={type === 'multi-edit' ? activities : undefined}
        />
        <AddActivityFooter
          printAfterSave={printAfterSave}
          setPrintAfterSave={setPrintAfterSave}
        />
      </form>
    </ModalLayout>
  )
}

export default AddActivityModal
